import { memo, useCallback, useEffect, useMemo, useState } from "react";

import { useRect } from "~/hooks/useRect";

import { ResizeableColumns } from "../resizableColumns/ResizeableColumns";
import { WatchViewMediaMode, WatchViewViewMode } from "./WatchViewManager";

import type { ReactNode } from "react";
import type { WatchViewLayoutEvent } from "./WatchView";

// This width * 2 plus the separator matches the custom
// media query for WatchView: 1127px
const ELEMENT_MIN_WIDTH = 555;

export interface WatchViewStageProps {
  mediaMode?: WatchViewMediaMode;
  viewMode?: WatchViewViewMode;
  children?: ReactNode;
  onLayout?: (event: WatchViewLayoutEvent) => void;
}

export const WatchViewStage = memo(function WatchViewStage({
  mediaMode,
  viewMode,
  children,
  onLayout,
}: WatchViewStageProps) {
  const [ref, DOMRect] = useRect<HTMLDivElement>();

  const [isSingleColumn, setIsSingleColumn] = useState(true);

  const forceSingleColumn = useMemo(() => {
    // return true;
    return (
      mediaMode === WatchViewMediaMode.Audio ||
      (viewMode && viewMode !== WatchViewViewMode.Splitscreen)
    );
  }, [mediaMode, viewMode]);

  const handleColumnReflow = useCallback((singleColumn: boolean) => {
    setIsSingleColumn(singleColumn);
  }, []);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (ref.current) {
        onLayout?.({ DOMRect, element: ref.current });
      }
    });
  }, [DOMRect, onLayout, ref]);

  return (
    <div
      ref={ref}
      className="WatchViewStage"
      data-watch-view-stage-single-column={isSingleColumn}
    >
      <ResizeableColumns
        style={{ width: "100%", height: "100%" }}
        forceSingleColumn={forceSingleColumn}
        minFirstColumnWidth={ELEMENT_MIN_WIDTH}
        minSecondColumnWidth={ELEMENT_MIN_WIDTH}
        initPosition={0.5}
        onColumnReflow={handleColumnReflow}
      >
        {children}
      </ResizeableColumns>
    </div>
  );
});
